import React from 'react'
import { useNavigate } from 'react-router-dom'
import InitialAvatar from 'shared/components/InitialAvatar/InitialAvatar'
import { ROUTES } from 'shared/config/routes'
import { formatFeedbackCardTimeShort } from '../../utils/feedbackMapping'
import {
	dashboardPriorityBackState,
	navigateToStudentProfileFromPriority,
} from '../../utils/priorityCardUi'
import { ReactComponent as CheckIcon } from 'assets/icons/check.svg'
import { ReactComponent as ClockIcon } from 'assets/icons/clock.svg'
import { ReactComponent as InfoIcon } from 'assets/icons/info.svg'
import { ReactComponent as EyeIcon } from 'assets/icons/openedEye.svg'
import { ReactComponent as PenIcon } from 'assets/icons/pen.svg'
import { ReactComponent as ExternalIcon } from 'assets/icons/open-external.svg'
import s from './RecommendationCard.module.scss'

const MS_IN_MIN = 1000 * 60
const MS_IN_HOUR = MS_IN_MIN * 60
const MS_IN_DAY = MS_IN_HOUR * 24

const formatCompactTime = value => {
	if (!value) return ''
	const date = new Date(value)
	if (Number.isNaN(date.getTime())) return ''
	const diffMs = Math.max(0, Date.now() - date.getTime())
	if (diffMs < MS_IN_HOUR) {
		const mins = Math.max(1, Math.floor(diffMs / MS_IN_MIN))
		return `${mins}m ago`
	}
	if (diffMs < MS_IN_DAY) {
		const hours = Math.max(1, Math.floor(diffMs / MS_IN_HOUR))
		return `${hours}h ago`
	}
	const days = Math.max(1, Math.floor(diffMs / MS_IN_DAY))
	if (days === 1) return '1d ago'
	if (days < 7) return `${days}d ago`
	return formatFeedbackCardTimeShort(value)
}

const STATUS_META = {
	uploaded: {
		label: 'Uploaded',
		tone: 'green',
		Icon: CheckIcon,
		actionLabel: 'View',
		actionTone: 'green',
		ActionIcon: EyeIcon,
	},
	requested: {
		label: 'Requested',
		tone: 'purple',
		Icon: ClockIcon,
		actionLabel: 'Create',
		actionTone: 'purple',
		ActionIcon: PenIcon,
	},
	multiple: {
		label: 'Multiple LORs',
		tone: 'orange',
		Icon: InfoIcon,
		actionLabel: 'Review LORs',
		actionTone: 'orange',
		ActionIcon: EyeIcon,
	},
}

const RecommendationCard = ({
	item,
	sourceTab = null,
	onView,
	onCreate,
	onReview,
}) => {
	const navigate = useNavigate()
	const statusKey = String(item?.status || 'uploaded').toLowerCase()
	const meta = STATUS_META[statusKey] || STATUS_META.uploaded
	const StatusIcon = meta.Icon
	const ActionIcon = meta.ActionIcon
	const dateLabel = formatCompactTime(item?.lastUpdatedAt) || 'Recently'
	const newCount = Number(item?.newCount) || 0
	const actionLabel =
		statusKey === 'multiple' && newCount > 0
			? `${meta.actionLabel} (${newCount})`
			: meta.actionLabel

	const handleOpenApplication = e => {
		e.stopPropagation()
		const appId = item?.applicationId || item?.applicationTargetId
		if (!appId) return
		navigate(`${ROUTES.APPLICATIONS}/${encodeURIComponent(appId)}`, {
			state: dashboardPriorityBackState(sourceTab),
		})
	}

	const handleOpenStudent = e => {
		e.stopPropagation()
		navigateToStudentProfileFromPriority(
			navigate,
			item?.studentId,
			sourceTab,
		)
	}

	const handlePrimary = e => {
		e.stopPropagation()
		if (statusKey === 'uploaded') onView?.(item)
		else if (statusKey === 'requested') onCreate?.(item)
		else onReview?.(item)
	}

	const pillClass = [s.pill, s[`pill_${meta.tone}`]].filter(Boolean).join(' ')
	const actionClass = [s.actionBtn, s[`action_${meta.actionTone}`]]
		.filter(Boolean)
		.join(' ')
	const studentName = item?.studentName || 'Student'

	return (
		<article className={s.card}>
			<div className={s.header}>
				<div className={s.headerLead}>
					<div className={s.avatar}>
						<InitialAvatar
							size={40}
							name={studentName}
							studentId={item?.studentId}
							photoUrl={item?.studentAvatarUrl}
							avatarSource={
								item?.studentAvatarUrl ? 'photo' : undefined
							}
							onClick={handleOpenStudent}
						/>
					</div>
					<div className={s.identity}>
						<button
							type="button"
							className={s.name}
							onClick={handleOpenStudent}
						>
							{studentName}
						</button>
						<span className={s.date}>{dateLabel}</span>
					</div>
				</div>
				<span className={pillClass}>
					<StatusIcon />
					{meta.label}
				</span>
			</div>

			<div className={s.appBlock}>
				{item?.universityName ? (
					<span className={s.university}>{item.universityName}</span>
				) : null}
				{item?.programName ? (
					<span className={s.program}>{item.programName}</span>
				) : null}
			</div>

			<div className={s.footer}>
				<button
					type="button"
					className={s.openBtn}
					onClick={handleOpenApplication}
					disabled={!item?.applicationId && !item?.applicationTargetId}
				>
					<ExternalIcon />
					Open Application
				</button>
				<button type="button" className={actionClass} onClick={handlePrimary}>
					<ActionIcon />
					{actionLabel}
				</button>
			</div>
		</article>
	)
}

export default RecommendationCard
